import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Upload, Link as LinkIcon, FileText, CheckCircle2, AlertCircle, Package, X, RefreshCw } from 'lucide-react';
import type { Product } from '../types';

type ImportMode = 'url' | 'csv' | 'xml';
type ImportStatus = 'idle' | 'loading' | 'preview' | 'importing' | 'done' | 'error' | 'idle_error';

interface ImportRow {
  name: string; price: number | string; category?: string; subcategory?: string;
  supplier?: string; description?: string; brand?: string; currency?: string;
  sku?: string; inStock?: boolean; image?: string; [key: string]: unknown;
}

interface ParseResult { rows: ImportRow[]; warnings: string[]; }

// ─── CSV parser (simple, handles quoted fields) ──────────────────────────────
function parseCSV(text: string): ParseResult {
  const lines = text.trim().split(/\r?\n/).filter(l => l.trim());
  if (lines.length < 2) return { rows: [], warnings: ['Fișierul CSV este gol sau nu are date.'] };
  const sep = lines[0].includes(';') ? ';' : ',';
  const parseRow = (line: string): string[] => {
    const result: string[] = [];
    let cur = '', inQ = false;
    for (let i = 0; i < line.length; i++) {
      const c = line[i];
      if (c === '"' && !inQ) { inQ = true; continue; }
      if (c === '"' && inQ) { if (line[i + 1] === '"') { cur += '"'; i++; } else inQ = false; continue; }
      if (c === sep && !inQ) { result.push(cur.trim()); cur = ''; continue; }
      cur += c;
    }
    result.push(cur.trim());
    return result;
  };
  const headers = parseRow(lines[0]).map(h => h.toLowerCase().replace(/\s+/g, '_'));
  const warnings: string[] = [];
  const rows: ImportRow[] = [];
  for (let i = 1; i < lines.length; i++) {
    const vals = parseRow(lines[i]);
    if (vals.length < 2) continue;
    const obj: Record<string, unknown> = {};
    headers.forEach((h, hi) => { obj[h] = vals[hi] || ''; });
    // Normalize common header aliases
    const get = (...ks: string[]) => ks.map(k => obj[k]).find(v => v !== undefined && v !== '') || '';
    const name = String(get('name', 'denumire', 'produs', 'titlu', 'product_name') || '').trim();
    if (!name) { warnings.push(`Rândul ${i + 1}: fără denumire — ignorat.`); continue; }
    const price = parseFloat(String(get('price', 'pret', 'preț', 'sale_price', 'regular_price') || '0').replace(',', '.')) || 0;
    rows.push({
      name, price,
      category: String(get('category', 'categorie', 'type', 'tip') || ''),
      subcategory: String(get('subcategory', 'subcategorie', 'subcat') || ''),
      supplier: String(get('supplier', 'furnizor', 'brand', 'producator', 'producător') || ''),
      brand: String(get('brand', 'marca', 'marcă') || ''),
      description: String(get('description', 'descriere', 'desc', 'short_description') || ''),
      currency: String(get('currency', 'moneda', 'monedă') || 'RON'),
      sku: String(get('sku', 'cod', 'part_number', 'mpn') || ''),
      image: String(get('image', 'imagine', 'image_url', 'photo') || ''),
      inStock: String(get('in_stock', 'stoc', 'disponibil', 'availability') || '1') !== '0',
    });
  }
  return { rows, warnings };
}

// ─── XML parser ──────────────────────────────────────────────────────────────
function parseXML(text: string): ParseResult {
  const warnings: string[] = [];
  const rows: ImportRow[] = [];
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(text, 'application/xml');
    const items = doc.querySelectorAll('item, product, Product, row');
    if (items.length === 0) { warnings.push('Nu s-au găsit elemente <item>, <product> sau <row> în XML.'); return { rows, warnings }; }
    const getText = (el: Element, ...tags: string[]) => {
      for (const t of tags) {
        const node = el.querySelector(t);
        if (node?.textContent?.trim()) return node.textContent.trim();
      }
      return '';
    };
    items.forEach((item, i) => {
      const name = getText(item, 'name', 'Name', 'denumire', 'title', 'Title', 'product_name');
      if (!name) { warnings.push(`Element ${i + 1}: fără denumire — ignorat.`); return; }
      const priceStr = getText(item, 'price', 'Price', 'pret', 'sale_price', 'regular_price');
      rows.push({
        name, price: parseFloat(priceStr.replace(',', '.')) || 0,
        category: getText(item, 'category', 'Category', 'categorie', 'type', 'Type'),
        subcategory: getText(item, 'subcategory', 'Subcategory', 'subcategorie'),
        supplier: getText(item, 'supplier', 'Supplier', 'furnizor', 'brand', 'Brand'),
        brand: getText(item, 'brand', 'Brand', 'marca'),
        description: getText(item, 'description', 'Description', 'descriere', 'short_description'),
        currency: getText(item, 'currency', 'Currency', 'moneda') || 'RON',
        sku: getText(item, 'sku', 'SKU', 'cod', 'part_number', 'mpn'),
        image: getText(item, 'image', 'Image', 'image_url', 'imagine', 'photo', 'picture'),
        inStock: getText(item, 'in_stock', 'availability', 'stoc') !== '0',
      });
    });
  } catch {
    warnings.push('Eroare la parsare XML — verificați că fișierul este XML valid.');
  }
  return { rows, warnings };
}

// ─── Slugify helper ───────────────────────────────────────────────────────────
function slugify(s: string) {
  return s.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
}

export default function ProductsImportPage() {
  const navigate = useNavigate();
  const fileRef = useRef<HTMLInputElement>(null);
  const [mode, setMode] = useState<ImportMode>('url');
  const [url, setUrl] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'preview' | 'importing' | 'done' | 'error'>('idle');
  const [parseResult, setParseResult] = useState<ParseResult | null>(null);
  const [selectedRows, setSelectedRows] = useState<Set<number>>(new Set());
  const [importDone, setImportDone] = useState(0);
  const [error, setError] = useState('');
  const [fileContent, setFileContent] = useState('');
  const [fileName, setFileName] = useState('');

  const isLoading = status === 'loading';
  const isImporting = status === 'importing';

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFileName(f.name);
    const reader = new FileReader();
    reader.onload = ev => setFileContent(String(ev.target?.result || ''));
    reader.readAsText(f, 'UTF-8');
  };

  const handleParse = () => {
    setError('');
    if (mode === 'csv' || mode === 'xml') {
      if (!fileContent) { setError('Selectați un fișier mai întâi.'); return; }
      const result = mode === 'csv' ? parseCSV(fileContent) : parseXML(fileContent);
      setParseResult(result);
      setSelectedRows(new Set(result.rows.map((_, i) => i)));
      setStatus('preview');
      return;
    }
    // URL mode
    if (!url.trim()) { setError('Introduceți un URL valid.'); return; }
    setStatus('loading');
    fetch(`/php-backend/cms-api/import-products.php?url=${encodeURIComponent(url)}`)
      .then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .then((data: { rows?: ImportRow[]; error?: string }) => {
        if (data.error) throw new Error(data.error);
        const rows = data.rows || [];
        setParseResult({ rows, warnings: rows.length === 0 ? ['Feed-ul nu a returnat produse.'] : [] });
        setSelectedRows(new Set(rows.map((_, i) => i)));
        setStatus('preview');
      })
      .catch(e => {
        setError(`Eroare la fetch URL: ${e.message}. Verificați că URL-ul este accesibil și returnează JSON/XML valid.`);
        setStatus('idle');
      });
  };

  const toggleRow = (i: number) => setSelectedRows(s => {
    const n = new Set(s);
    n.has(i) ? n.delete(i) : n.add(i);
    return n;
  });

  const toggleAll = () => {
    if (!parseResult) return;
    setSelectedRows(s => s.size === parseResult.rows.length ? new Set() : new Set(parseResult.rows.map((_, i) => i)));
  };

  const doImport = async () => {
    if (!parseResult || selectedRows.size === 0) return;
    setStatus('importing');
    const toImport = Array.from(selectedRows).map(i => {
      const r = parseResult.rows[i];
      const id = `import-${Date.now()}-${i}`;
      const name = String(r.name || 'Produs importat');
      return {
        id, name, slug: slugify(name),
        type: 'other' as const, category: String(r.category || ''), categoryLabel: String(r.category || ''),
        subcategory: String(r.subcategory || ''), subcategoryLabel: String(r.subcategory || ''),
        brand: String(r.brand || r.supplier || ''),
        supplier: String(r.supplier || ''),
        description: String(r.description || ''),
        price: Number(r.price) || 0,
        priceTva: Math.round(Number(r.price) * 1.21 * 100) / 100,
        currency: String(r.currency || 'RON') as 'RON' | 'EUR',
        image: String(r.image || ''),
        inStock: r.inStock !== false,
        status: 'active' as const,
        period: 'once' as const,
        highlighted: false,
        upsellIds: [] as string[],
        crosssellIds: [] as string[],
        requiresShipping: false,
        purchasePrice: undefined as number | undefined,
        salePrice: undefined as number | undefined,
        margin: undefined as number | undefined,
        features: [] as string[],
        specs: {} as Record<string, string>,
        seo: { title: name, description: String(r.description || '').slice(0, 160), keywords: '' },
        updatedAt: new Date().toISOString().split('T')[0],
      };
    });
    try {
      const res = await fetch('/php-backend/cms-api/import-products.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ products: toImport as unknown[] }),
      });
      const data = await res.json();
      if (data.error) throw new Error(data.error);
      setImportDone(data.imported || toImport.length);
      setStatus('done');
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Eroare necunoscută';
      setError(`Eroare la import: ${msg}`);
      setStatus('error');
    }
  };

  const reset = () => {
    setStatus('idle'); setParseResult(null); setSelectedRows(new Set());
    setError(''); setUrl(''); setFileContent(''); setFileName(''); setImportDone(0);
    if (fileRef.current) fileRef.current.value = '';
  };

  return (
    <div className="space-y-5 max-w-4xl">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button onClick={() => navigate('/products')}
          className="p-2 rounded-lg text-white/40 hover:text-white hover:bg-white/5 transition-all">
          <ArrowLeft size={18} />
        </button>
        <div>
          <h1 className="text-xl font-bold text-white">Import Produse</h1>
          <p className="text-xs text-white/40 mt-0.5">Importați produse din feed URL, CSV sau XML</p>
        </div>
      </div>

      {status === 'done' ? (
        <div className="text-center py-16 space-y-4">
          <CheckCircle2 size={48} className="mx-auto text-green-400" />
          <h2 className="text-lg font-bold text-white">{importDone} produse importate cu succes!</h2>
          <p className="text-sm text-white/40">Produsele au fost adăugate în catalogul dvs.</p>
          <div className="flex justify-center gap-3">
            <button onClick={() => navigate('/products')}
              className="bg-[#295CA8] hover:bg-[#3D72B8] text-white px-5 py-2 rounded-lg text-sm font-medium transition-colors">
              ← Mergi la Produse
            </button>
            <button onClick={reset}
              className="border border-white/10 text-white/50 hover:text-white px-5 py-2 rounded-lg text-sm transition-colors">
              <RefreshCw size={14} className="inline mr-1.5" />Nou import
            </button>
          </div>
        </div>
      ) : (
        <>
          {/* Mode tabs */}
          <div className="flex gap-2 bg-white/5 p-1 rounded-xl w-fit">
            {([
              { k: 'url', icon: LinkIcon, label: 'Feed URL' },
              { k: 'csv', icon: FileText, label: 'Fișier CSV' },
              { k: 'xml', icon: FileText, label: 'Fișier XML' },
            ] as const).map(({ k, icon: Icon, label }) => (
              <button key={k} onClick={() => { setMode(k); reset(); }}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${mode === k ? 'bg-[#295CA8] text-white' : 'text-white/40 hover:text-white'}`}>
                <Icon size={14} /> {label}
              </button>
            ))}
          </div>

          {/* Input area */}
          {(status === 'idle' || status === 'error') ? (
            <div className="bg-[#07090f] border border-white/10 rounded-xl p-6 space-y-4">
              {mode === 'url' && (
                <div>
                  <label className="block text-sm font-medium text-white/70 mb-2">URL Feed furnizor</label>
                  <p className="text-xs text-white/30 mb-3">
                    Suportate: JSON (array de produse sau {"{ products: [...] }"}), XML (elemente &lt;item&gt; / &lt;product&gt;), RSS cu produse WooCommerce.
                  </p>
                  <input value={url} onChange={e => setUrl(e.target.value)}
                    placeholder="https://furnizor.ro/export/products.json"
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-sm text-white placeholder-white/20 focus:outline-none focus:border-[#295CA8] transition-colors" />
                </div>
              )}
              {(mode === 'csv' || mode === 'xml') && (
                <div>
                  <label className="block text-sm font-medium text-white/70 mb-2">
                    Fișier {mode.toUpperCase()}
                  </label>
                  {mode === 'csv' && (
                    <p className="text-xs text-white/30 mb-3">
                      Coloane detectate automat. Aliasuri suportate: <code className="text-white/50">name/denumire/produs</code>, <code className="text-white/50">price/pret/preț</code>, <code className="text-white/50">category/categorie</code>, <code className="text-white/50">brand/furnizor</code>, <code className="text-white/50">description/descriere</code>. Separator: <code className="text-white/50">,</code> sau <code className="text-white/50">;</code>.
                    </p>
                  )}
                  {mode === 'xml' && (
                    <p className="text-xs text-white/30 mb-3">
                      Elemente suportate: <code className="text-white/50">&lt;item&gt;</code>, <code className="text-white/50">&lt;product&gt;</code>, <code className="text-white/50">&lt;row&gt;</code>. Taguri interne: <code className="text-white/50">&lt;name&gt;/&lt;denumire&gt;</code>, <code className="text-white/50">&lt;price&gt;/&lt;pret&gt;</code>, etc.
                    </p>
                  )}
                  <div className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors cursor-pointer ${fileContent ? 'border-[#295CA8]/50 bg-[#295CA8]/5' : 'border-white/10 hover:border-white/25'}`}
                    onClick={() => fileRef.current?.click()}>
                    <input ref={fileRef} type="file" accept={mode === 'csv' ? '.csv,text/csv' : '.xml,application/xml,text/xml'}
                      className="hidden" onChange={handleFileChange} />
                    <Upload size={28} className={`mx-auto mb-2 ${fileContent ? 'text-[#4ab8ff]' : 'text-white/20'}`} />
                    {fileContent ? (
                      <div>
                        <p className="text-sm font-medium text-white">{fileName}</p>
                        <p className="text-xs text-white/40 mt-1">{fileContent.length.toLocaleString()} bytes • Click pentru a înlocui</p>
                      </div>
                    ) : (
                      <div>
                        <p className="text-sm text-white/50">Click sau drag & drop fișier {mode.toUpperCase()}</p>
                        <p className="text-xs text-white/25 mt-1">Format: .{mode}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {(status === 'idle' || !!error) && error && (
                <div className="flex items-start gap-2 p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-xs text-red-400">
                  <AlertCircle size={14} className="flex-shrink-0 mt-0.5" /> {error}
                </div>
              )}

              <div className="flex justify-end">
                <button onClick={handleParse} disabled={isLoading}
                  className="inline-flex items-center gap-2 bg-[#295CA8] hover:bg-[#3D72B8] text-white px-6 py-2.5 rounded-lg text-sm font-medium transition-colors disabled:opacity-50">
                  {isLoading ? (
                    <><div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" /> Se parsează...</>
                  ) : (
                    <><Package size={15} /> Parsează și previzualizează</>
                  )}
                </button>
              </div>
            </div>
          ) : null}

          {/* Preview table */}
          {status === 'preview' && parseResult && (
            <div className="space-y-4">
              {/* Warnings */}
              {parseResult.warnings.length > 0 && (
                <div className="p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/20 space-y-1">
                  {parseResult.warnings.map((w, i) => (
                    <div key={i} className="flex items-start gap-2 text-xs text-yellow-400">
                      <AlertCircle size={12} className="flex-shrink-0 mt-0.5" /> {w}
                    </div>
                  ))}
                </div>
              )}

              {/* Stats */}
              <div className="flex items-center justify-between">
                <div className="text-sm text-white/60">
                  <strong className="text-white">{parseResult.rows.length}</strong> produse detectate •{' '}
                  <strong className="text-[#4ab8ff]">{selectedRows.size}</strong> selectate pentru import
                </div>
                <div className="flex gap-2">
                  <button onClick={reset} className="flex items-center gap-1.5 text-xs text-white/40 hover:text-white border border-white/10 px-3 py-1.5 rounded-lg transition-all">
                    <X size={12} /> Anulează
                  </button>
                  <button onClick={doImport} disabled={selectedRows.size === 0 || isImporting}
                    className="inline-flex items-center gap-2 bg-[#295CA8] hover:bg-[#3D72B8] text-white px-5 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50">
                    {isImporting ? (
                    <><div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" /> Se importă...</>
                  ) : selectedRows.size > 0 ? (
                    <><Upload size={14} /> Importă {selectedRows.size} produse</>
                  ) : (
                    <><Upload size={14} /> Selectați produse</>
                    )}
                  </button>
                </div>
              </div>

              {/* Table */}
              <div className="bg-[#07090f] border border-white/10 rounded-xl overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[700px] text-sm">
                    <thead>
                      <tr className="border-b border-white/10">
                        <th className="px-4 py-3 text-left">
                          <input type="checkbox" checked={selectedRows.size === parseResult.rows.length}
                            onChange={toggleAll} className="accent-[#295CA8]" />
                        </th>
                        <th className="px-4 py-3 text-left text-xs text-white/40 font-medium uppercase tracking-wide">Denumire</th>
                        <th className="px-4 py-3 text-left text-xs text-white/40 font-medium uppercase tracking-wide">Preț</th>
                        <th className="px-4 py-3 text-left text-xs text-white/40 font-medium uppercase tracking-wide">Categorie</th>
                        <th className="px-4 py-3 text-left text-xs text-white/40 font-medium uppercase tracking-wide hidden md:table-cell">Brand / Furnizor</th>
                        <th className="px-4 py-3 text-left text-xs text-white/40 font-medium uppercase tracking-wide hidden lg:table-cell">Stoc</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-white/5">
                      {parseResult.rows.map((row, i) => (
                        <tr key={i} className={`transition-colors cursor-pointer ${selectedRows.has(i) ? 'bg-[#295CA8]/5' : 'opacity-40'} hover:opacity-100`}
                          onClick={() => toggleRow(i)}>
                          <td className="px-4 py-3">
                            <input type="checkbox" checked={selectedRows.has(i)} onChange={() => toggleRow(i)} className="accent-[#295CA8]" onClick={e => e.stopPropagation()} />
                          </td>
                          <td className="px-4 py-3">
                            <div className="text-white font-medium leading-tight line-clamp-1">{row.name}</div>
                            {row.description && <div className="text-xs text-white/30 mt-0.5 line-clamp-1">{row.description}</div>}
                          </td>
                          <td className="px-4 py-3 text-white font-mono text-xs">{Number(row.price).toFixed(2)} {row.currency || 'RON'}</td>
                          <td className="px-4 py-3 text-xs text-white/50">{row.category || '—'}{row.subcategory ? ` › ${row.subcategory}` : ''}</td>
                          <td className="px-4 py-3 text-xs text-white/50 hidden md:table-cell">{row.brand || row.supplier || '—'}</td>
                          <td className="px-4 py-3 hidden lg:table-cell">
                            <span className={`text-xs px-1.5 py-0.5 rounded ${row.inStock !== false ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
                              {row.inStock !== false ? 'Da' : 'Nu'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
